/*
  /reset-password?token=… — user lands here from the email. Validates the
  token on submit and sets a new password.
*/
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { auth } from "../lib/authApi";

export default function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await auth.reset(token, password);
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Unable to reset password");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="max-w-md mx-auto py-8" data-testid="reset-no-token">
        <h1 className="font-display tracking-tighter text-2xl mb-2">Invalid reset link</h1>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          The reset link is missing its token. Request a new one from the sign-in screen.
        </p>
        <Link to="/login" className="text-sm hover:underline" style={{ color: "var(--primary-ink)" }}>← Back to sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto w-full py-8" data-testid="reset-page">
      <h1 className="font-display tracking-tighter text-3xl mb-1" style={{ color: "var(--primary-ink)" }}>
        Choose a new password
      </h1>
      <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>
        Pick something at least 6 characters long. You'll be signed in with it after this step.
      </p>
      <div className="card-tactile rounded-2xl p-5">
        {done ? (
          <div className="flex items-start gap-3 rounded-lg p-3"
               style={{ background: "color-mix(in srgb, var(--success) 12%, transparent)", color: "var(--success)" }}>
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-sm">Password updated. Redirecting you to sign in…</div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="label-caps block mb-1">New password</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--text-muted)" }} />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full pl-9 pr-3 h-10 rounded-lg border bg-transparent text-sm"
                  style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
                />
              </div>
            </label>
            {err && (
              <div data-testid="reset-error" className="text-sm rounded-lg px-3 py-2"
                   style={{ background: "color-mix(in srgb, var(--error) 12%, transparent)", color: "var(--error)" }}>
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full h-10 rounded-lg text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: "var(--primary)" }}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
